// Master plan configuration — single source of truth for plan display content,
// feature messaging, and pricing metadata used across UI and checkout handoff.
// Lemon Squeezy variant IDs live in env vars resolved via src/lib/billing/lemon.ts.

import type { PlanTier } from '@/types'

export type FeatureGroup = {
  title: string
  items: string[]
}

export type PlanConfig = {
  id: PlanTier
  name: string
  tagline: string
  audience: string
  order: number
  monthlyPriceZA: number
  annualDiscountPct?: number
  mostPopular?: boolean
  featureGroups: FeatureGroup[]
  upgradeReason: string
}

export const PLANS: PlanConfig[] = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'Try it, no card needed',
    audience: 'For trying BuilderBuddi and getting your first jobs, quotes, and invoices flowing.',
    order: 0,
    monthlyPriceZA: 0,
    upgradeReason: 'Upgrade to Solo for unlimited jobs, quotes, and a proper daily system.',
    featureGroups: [
      {
        title: 'Core workflow',
        items: [
          '5 active jobs at any time',
          '5 quotes & 5 invoices per month',
          'Core calculators (concrete, paint, tile)',
          'Field notebook',
          'WhatsApp client sharing',
        ],
      },
      {
        title: 'AI & automation',
        items: ['3 AI credits/month — smart create basics'],
      },
      {
        title: 'Documents',
        items: ['Standard PDF quotes & invoices'],
      },
      {
        title: 'Best for',
        items: ['Testing BuilderBuddi before committing'],
      },
    ],
  },
  {
    id: 'solo',
    name: 'Solo',
    tagline: 'For the one-person trade business',
    audience: 'For one-person trade businesses that need a proper daily system.',
    order: 1,
    monthlyPriceZA: 299,
    annualDiscountPct: 15,
    upgradeReason: 'Upgrade to Contractor for voice capture, advanced AI, supplier tracking, and more control.',
    featureGroups: [
      {
        title: 'Core workflow',
        items: [
          'Unlimited active jobs',
          'Unlimited quotes & invoices',
          'All 20+ calculators',
          'Field notebook',
          'WhatsApp client sharing & follow-ups',
        ],
      },
      {
        title: 'AI & automation',
        items: [
          '100 AI credits/month',
          'AI smart create for quotes & scopes',
        ],
      },
      {
        title: 'Business control',
        items: [
          'Branded documents & cover letters',
          'Business analytics dashboard',
          'Public quote acceptance page',
          'Single rate card',
        ],
      },
      {
        title: 'Best for',
        items: ['One-person trade businesses running a proper daily system'],
      },
    ],
  },
  {
    id: 'contractor',
    name: 'Contractor',
    tagline: 'Speed, AI, and full control',
    audience: 'For growing operators who need more speed, stronger AI, and more control.',
    order: 2,
    monthlyPriceZA: 599,
    annualDiscountPct: 25,
    mostPopular: true,
    upgradeReason: 'Upgrade to Crew for multi-user scale, shared workspace, and team operations.',
    featureGroups: [
      {
        title: 'Core workflow',
        items: [
          'Everything in Solo',
          'Unlimited everything',
          'All advanced business calculators',
        ],
      },
      {
        title: 'AI & automation',
        items: [
          '500 AI credits/month',
          'AI voice capture for site notes',
          'Advanced AI scope & suggestion engine',
          'Quote intelligence',
        ],
      },
      {
        title: 'Business control',
        items: [
          'Cover pages & portfolio pages',
          'Multiple rate cards',
          'Supplier price tracking',
          'Pricing memory (auto-fill from history)',
          'Full analytics & job reporting',
        ],
      },
      {
        title: 'Best for',
        items: ['Growing operators who need speed, stronger AI, and full control'],
      },
    ],
  },
  {
    id: 'crew',
    name: 'Crew',
    tagline: 'Multi-user scale for teams',
    audience: 'For teams that need multi-user scale, more power, and deeper operational support.',
    order: 3,
    monthlyPriceZA: 999,
    annualDiscountPct: 30,
    upgradeReason: 'You are on the highest plan.',
    featureGroups: [
      {
        title: 'Core workflow',
        items: [
          'Everything in Contractor',
          'Up to 5 users in one workspace',
        ],
      },
      {
        title: 'AI & automation',
        items: [
          '1,500 AI credits/month (shared)',
          'All AI features enabled',
        ],
      },
      {
        title: 'Business control',
        items: [
          'Shared business workspace',
          'Role-based access & permissions',
          'Shared rate cards & supplier data',
          'Team analytics & activity tracking',
        ],
      },
      {
        title: 'Best for',
        items: ['Teams that need multi-user scale and deeper operational control'],
      },
    ],
  },
]

export const PLAN_MAP = Object.fromEntries(PLANS.map(p => [p.id, p])) as Record<PlanTier, PlanConfig>

export function getPlanConfig(id: PlanTier): PlanConfig {
  return PLAN_MAP[id] ?? PLAN_MAP.free
}
